'use client'

import { useState, useEffect, useCallback } from 'react'
import { getImageUrl, getVideoUrl, sanityAssetResolvable } from '../lib/sanity'
import Image from 'next/image'
import { useTheme } from './ThemeProvider'
import Header from './Header'
import CinemaProjectView from './CinemaProjectView'

interface ProjectData {
  id: string
  brand: string
  title: string
  number: string
  info?: string
  bgColor: string
  sanityProject?: any
}

interface ClientHomepageProps {
  projects: ProjectData[]
}

export default function ClientHomepage({ projects }: ClientHomepageProps) {
  const [activeProject, setActiveProject] = useState(0)
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
  const [isClient, setIsClient] = useState(false)
  const [lastScrollTime, setLastScrollTime] = useState(0)
  const { theme, setTheme } = useTheme()
  const [isMuted, setIsMuted] = useState(true)
  const [showInfo, setShowInfo] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(true)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [showCinema, setShowCinema] = useState(false)
  const [cinemaNav, setCinemaNav] = useState({ projectIndex: 0, mediaIndex: 0 })

  useEffect(() => {
    setIsClient(true)
  }, []);

  const handleCinemaNext = useCallback(() => {
    setCinemaNav((s) => {
      const proj = projects[s.projectIndex]?.sanityProject
      const len = proj?.media?.length ?? 0
      if (len > 0 && s.mediaIndex < len - 1) {
        return { ...s, mediaIndex: s.mediaIndex + 1 }
      }
      if (s.projectIndex < projects.length - 1) {
        return { projectIndex: s.projectIndex + 1, mediaIndex: 0 }
      }
      return s
    })
  }, [projects])

  const handleCinemaPrev = useCallback(() => {
    setCinemaNav((s) => {
      if (s.mediaIndex > 0) {
        return { ...s, mediaIndex: s.mediaIndex - 1 }
      }
      if (s.projectIndex > 0) {
        const prevProj = projects[s.projectIndex - 1]?.sanityProject
        const lastIdx =
          prevProj?.media?.length && prevProj.media.length > 0
            ? prevProj.media.length - 1
            : 0
        return { projectIndex: s.projectIndex - 1, mediaIndex: lastIdx }
      }
      return s
    })
  }, [projects])

  const handleCloseCinema = useCallback(() => {
    setShowCinema(false)
  }, [])

  const handleOpenCinema = useCallback((projectIndex: number) => {
    if (!projects[projectIndex]?.sanityProject) return
    setCinemaNav({ projectIndex, mediaIndex: 0 })
    setShowCinema(true)
  }, [projects])

  useEffect(() => {
    if (!showCinema) return
    setActiveProject(cinemaNav.projectIndex)
    setCurrentMediaIndex(cinemaNav.mediaIndex)
  }, [showCinema, cinemaNav.projectIndex, cinemaNav.mediaIndex])

  const getCurrentProjectMedia = () => {
    const project = projects[activeProject]
    if (project.sanityProject?.media && project.sanityProject.media.length > 0) {
      return project.sanityProject.media[currentMediaIndex] || project.sanityProject.media[0]
    }
    return null
  }
  
  const isVideo = (media: {
    type?: string
    video?: { asset?: { _ref?: string; url?: string } }
  } | null) =>
    !!(media?.video?.asset?._ref || media?.video?.asset?.url) ||
    (media?.type === 'video' && !!media.video)

  /** Poster for LCP when hero is video (Sanity coverImage or still image). */
  const getVideoPosterUrl = (
    media: ReturnType<typeof getCurrentProjectMedia>,
  ): string | null => {
    if (!media || !isVideo(media)) return null
    const cover = 'coverImage' in media ? media.coverImage : undefined
    if (cover?.asset && sanityAssetResolvable(cover.asset)) {
      const u = getImageUrl(cover, 720, undefined, 78)
      return u || null
    }
    const still = 'image' in media ? media.image : undefined
    if (still?.asset && sanityAssetResolvable(still.asset)) {
      const u = getImageUrl(still, 720, undefined, 78)
      return u || null
    }
    return null
  }

  const handleSmoothScroll = useCallback((deltaY: number) => {
    // Add scroll throttling to reduce sensitivity
    const now = Date.now()
    if (now - lastScrollTime < 200) return // Increased throttle for smoother scrolling
    
    // Increase threshold for less sensitive scrolling
    const threshold = 15 // Higher threshold for less sensitive scrolling
    if (Math.abs(deltaY) < threshold) return
    
    
    if (deltaY < 0 && activeProject < projects.length - 1) {
      setActiveProject(prev => prev + 1)
      setCurrentMediaIndex(0)
      setLastScrollTime(now)
    } else if (deltaY > 0 && activeProject > 0) {
      setActiveProject(prev => prev - 1)
      setCurrentMediaIndex(0)
      setLastScrollTime(now)
    }
  }, [activeProject, projects.length, lastScrollTime])

  // Wheel: in cinema mode, advance media / featured projects; otherwise home list scroll (left half)
  useEffect(() => {
    const handleGlobalWheel = (e: WheelEvent) => {
      if (showCinema) {
        e.preventDefault()
        const now = Date.now()
        if (now - lastScrollTime < 200) return
        const threshold = 15
        if (Math.abs(e.deltaY) < threshold) return
        if (e.deltaY < 0) {
          handleCinemaNext()
        } else {
          handleCinemaPrev()
        }
        setLastScrollTime(now)
        return
      }
      if (e.clientX < window.innerWidth / 2) {
        e.preventDefault()
        handleSmoothScroll(e.deltaY)
      }
    }

    window.addEventListener('wheel', handleGlobalWheel, { passive: false })

    return () => {
      window.removeEventListener('wheel', handleGlobalWheel)
    }
  }, [showCinema, lastScrollTime, handleSmoothScroll, handleCinemaNext, handleCinemaPrev])


  // Auto-close info when scrolling to next item
  useEffect(() => {
    setShowInfo(false)
  }, [activeProject])

  const currentMedia = getCurrentProjectMedia()
  const mediaUrl = currentMedia ? (currentMedia.video ? getVideoUrl(currentMedia.video) : getImageUrl(currentMedia.image)) : null
  const hasValidMedia = !!mediaUrl
  const videoPosterUrl = getVideoPosterUrl(currentMedia)

  useEffect(() => {
    setVideoLoaded(false)
  }, [activeProject, currentMediaIndex, mediaUrl])

  // Auto-play video when it loads
  useEffect(() => {
    if (isClient && hasValidMedia) {
      if (isVideo(currentMedia)) {
        const video = document.querySelector('video')
        if (video) {
          // Set video muted state to match user preference
          video.muted = isMuted
          video.play().then(() => {
            setIsVideoPlaying(true)
            setVideoLoaded(true)
          }).catch(() => {
            // Autoplay failed, video will need user interaction
            setIsVideoPlaying(false)
          })
        }
      }
    }
  }, [isClient, hasValidMedia, currentMediaIndex, activeProject, isMuted]);

  function renderHeader() {
    return <Header currentPage="home" />
  }

  const cinemaProject = projects[cinemaNav.projectIndex]?.sanityProject

  const featuredProjectsTotal = projects.filter((p) => p.sanityProject).length
  const featuredProjectPosition =
    featuredProjectsTotal > 0
      ? projects
          .slice(0, cinemaNav.projectIndex + 1)
          .filter((p) => p.sanityProject).length
      : 1

  return (
    <>
      {renderHeader()}
      <div 
        className={`page-container h-screen flex pointer-events-none ${theme === 'dark' ? 'bg-dark text-white' : 'bg-day text-primary'}`}
      >
                {/* Project List - Simple vertical layout */}
        <div 
          className="absolute inset-0 z-10 pointer-events-none md:pointer-events-auto md:relative md:h-full md:w-[calc(100vw-320px)] lg:w-[calc(100vw-calc(100vh*9/16*0.9))]"
          onWheel={(e) => {
            e.stopPropagation();
            handleSmoothScroll(e.deltaY);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            const touch = e.touches[0];
            (e.target as any).startY = touch.clientY;
            (e.target as any).startTime = Date.now();
            (e.target as any).lastY = touch.clientY;
          }}
          onTouchMove={(e) => {
            e.stopPropagation();
            const touch = e.touches[0];
            const startY = (e.target as any).startY;
            const lastY = (e.target as any).lastY;
            const startTime = (e.target as any).startTime;
            
            if (startY !== undefined && lastY !== undefined) {
              const deltaY = touch.clientY - lastY;
              const totalDeltaY = touch.clientY - startY;
              const deltaTime = Date.now() - startTime;
              
              // Only trigger if there's significant movement
              if (Math.abs(deltaY) > 8 && Math.abs(totalDeltaY) > 30) {
                handleSmoothScroll(deltaY * 1.5); // Reduced sensitivity
                (e.target as any).lastY = touch.clientY;
              }
            }
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            (e.target as any).startY = null;
            (e.target as any).lastY = null;
            (e.target as any).startTime = null;
          }}
          style={{ touchAction: 'none' }}
        >
          {/* Simple Vertical Project List - Fixed Container */}
          <div 
            className={`fixed left-0 top-1/3 md:top-1/2 transform -translate-y-1/2 pointer-events-auto ${
              theme === 'dark' ? 'text-white' : 'text-black'
            }`}
            style={{
              height: '100vh',
              width: '50vw',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingLeft: '8px' // Match global padding
            }}
          >
            {projects.map((project, index) => {
              const isActive = index === activeProject
              const distanceFromActive = Math.abs(activeProject - index)
              const opacity = Math.max(0, Math.min(1, 1 - distanceFromActive * 0.3))
              const blurRadius = Math.min(1, distanceFromActive * 0.3)
              
              return (
                <div
                  key={project.id}
                  className={`project-item absolute transition-all duration-500 ease-in-out cursor-pointer ${
                    isActive ? 'opacity-100' : 'opacity-60'
                  }`}
                  style={{
                    top: '50%',
                    transform: `translateY(-50%) translateY(${(index - activeProject) * 100}px)`,
                    opacity: opacity,
                    filter: `blur(${blurRadius}px)`,
                    pointerEvents: isActive ? 'auto' : 'none'
                  }}
                  onClick={() => {
                    setActiveProject(index)
                    setCurrentMediaIndex(0)
                    if (project.sanityProject) {
                      handleOpenCinema(index)
                    }
                  }}
                >
                  <div
                    className={`pointer-events-none flex flex-col leading-snug w-fit ${isActive ? 'pointer-events-auto' : ''}`}
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      backgroundColor: 'transparent',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none'
                    }}
                  >
                    <span className="uppercase">
                      <span className={`${isActive ? 'underline underline-offset-4' : ''}`}>{project.brand}</span> {project.number}
                    </span>
                      {project.info && (
                        <span 
                        className={`underline underline-offset-4 cursor-pointer ${
                          theme === 'dark' ? 'hover:text-gray-300' : 'hover:text-gray-600'
                          }`}
                        style={{ 
                          opacity: isActive ? 1 : 0,
                          WebkitTapHighlightColor: 'transparent',
                          WebkitTouchCallout: 'none',
                          WebkitUserSelect: 'none',
                          userSelect: 'none',
                          transition: 'none'
                        }}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setShowInfo(!showInfo)
                        }}
                        >
                          info
                        </span>
                      )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Info Text Block - In left container, aligned to the right */}
          {showInfo && projects[activeProject]?.sanityProject?.description && (
            <div className="absolute right-2 md:right-4 z-40 max-w-xs w-72 md:max-w-md md:w-80 transition-all duration-500 info-text-mobile"
                 style={{
                   animation: 'blurToFocus 0.5s ease-out forwards'
                 }}
            >
              <div className={`transition-colors text-right ${
                theme === 'dark' 
                  ? 'text-white' 
                  : 'text-black'
              }`}>
                <p className="text-sm leading-relaxed">
                  {projects[activeProject]?.sanityProject?.description}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Image Container - 9:16 aspect ratio floating screen */}
        <div 
          className="fixed bottom-2 right-2 z-5 w-[256px] h-[456px] md:top-2 md:bottom-2 md:w-[calc(100vh*9/16*0.9)] md:h-[calc(100vh-1rem)] bg-black overflow-hidden rounded-lg md:rounded-none pointer-events-auto"
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="relative h-full w-full">
            {hasValidMedia && isVideo(currentMedia) ? (
              <>
                {videoPosterUrl ? (
                  <Image
                    src={videoPosterUrl}
                    alt={`${projects[activeProject]?.title || 'Project'} preview`}
                    fill
                    className={`object-cover transition-opacity duration-500 ${
                      videoLoaded ? 'opacity-0' : 'opacity-100'
                    }`}
                    sizes="(max-width: 768px) 256px, 45vw"
                    priority
                  />
                ) : null}
                <video
                  key={`video-${activeProject}-${currentMediaIndex}`}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                    videoPosterUrl && !videoLoaded ? 'opacity-0' : 'opacity-100'
                  }`}
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  preload={videoPosterUrl ? 'metadata' : 'auto'}
                  poster={videoPosterUrl ?? undefined}
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  controls={false}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  aria-label={`Video preview for ${projects[activeProject]?.title || 'project'}`}
                  style={{
                    pointerEvents: 'none',
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!isVideoPlaying) {
                      const video = e.currentTarget
                      video.play()
                      setIsVideoPlaying(true)
                    }
                  }}
                  onPlay={() => {
                    setIsVideoPlaying(true)
                    setVideoLoaded(true)
                  }}
                  onPause={() => setIsVideoPlaying(false)}
                  onLoadedData={() => {
                    setVideoLoaded(true)
                  }}
                  onCanPlay={() => {
                    setVideoLoaded(true)
                  }}
                  onError={() => {
                    setVideoLoaded(false)
                    setIsVideoPlaying(false)
                  }}
                >
                  <source src={mediaUrl ?? undefined} type="video/mp4" />
                </video>
              </>
            ) : hasValidMedia && !isVideo(currentMedia) ? (
              <Image 
                src={mediaUrl!}
                alt={`${projects[activeProject]?.title || 'Project'} image by ${projects[activeProject]?.brand || 'NOLAN DANIEL WHITE'}`}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 256px, 45vw"
                priority
              />
            ) : (
              <div className={`w-full h-full ${projects[activeProject]?.bgColor || 'bg-gray-200'}`} />
            )}
          </div>
          {hasValidMedia && (
            <>
              {/* Custom Play Button - Desktop only, hidden on mobile since video autoplays */}
              {isVideo(currentMedia) && videoLoaded && !isVideoPlaying && (
                <div className="absolute bottom-2 right-2 z-[9999] pointer-events-auto hidden md:block">
              <button
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      const video = document.querySelector('video')
                      if (video) {
                        video.play()
                        setIsVideoPlaying(true)
                      }
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      const video = document.querySelector('video')
                      if (video) {
                        video.play()
                        setIsVideoPlaying(true)
                      }
                    }}
                className={`text-xs uppercase tracking-widest px-2 py-1 focus:outline-none transition-colors duration-300 ${
                  theme === 'dark' 
                        ? 'bg-dark text-white' 
                        : 'bg-day text-black'
                    }`}
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none'
                    }}
                  >
                    PLAY
                  </button>
                </div>
              )}
              
              {/* Audio Toggle - Bottom right when video is playing */}
              {isVideo(currentMedia) && isVideoPlaying && (
                <div className="absolute bottom-0 right-0 z-[99999] pointer-events-auto flex items-end" style={{ zIndex: 99999 }}>
                  <button
                    onTouchStart={(e) => {
                      console.log('Audio toggle touch start')
                      e.stopPropagation()
                    }}
                    onTouchEnd={(e) => {
                      console.log('Audio toggle touched')
                      e.stopPropagation()
                      e.preventDefault()
                      setIsMuted(!isMuted)
                    }}
                    onClick={(e) => {
                      console.log('Audio toggle clicked')
                      e.stopPropagation()
                      e.preventDefault()
                      setIsMuted(!isMuted)
                    }}
                    aria-label={isMuted ? 'Turn sound on' : 'Turn sound off'}
                    className={`text-xs uppercase tracking-widest px-1.5 py-0.5 focus:outline-none transition-colors duration-300 cursor-pointer touch-manipulation ${
                      theme === 'dark' 
                        ? 'bg-dark text-white' 
                        : 'bg-day text-black'
                    }`}
                    style={{ 
                      fontSize: '0.75rem', 
                      lineHeight: '1.5',
                      WebkitTapHighlightColor: 'transparent',
                      WebkitTouchCallout: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                      zIndex: 10000,
                      position: 'relative',
                      touchAction: 'manipulation'
                    }}
              >
                {isMuted ? 'SOUND ON' : 'SOUND OFF'}
              </button>
            </div>
              )}
            </>
          )}
        </div>
      </div>

      {showCinema && cinemaProject && (
        <div className="fixed inset-0 z-[400]">
          <CinemaProjectView
            project={cinemaProject}
            onClose={handleCloseCinema}
            onNext={handleCinemaNext}
            onPrev={handleCinemaPrev}
            currentMediaIndex={cinemaNav.mediaIndex}
            totalMedia={cinemaProject.media?.length ?? 0}
            nextProjectId={undefined}
            nextProject={undefined}
            featuredProjectPosition={featuredProjectPosition}
            featuredProjectsTotal={featuredProjectsTotal}
          />
        </div>
      )}
    </>
  )
}
 